import merge from "lodash/merge";
import { createSelector } from "reselect";
import { ConfigurationResponse, ConfigurationResponseProperties } from "@idw-types/data";
import {
	AXNUILayout,
	AXNUITheme
} from "@iddataweb/idw-common-ui/types";
import {
	DEFAULT_AXNUI_CONTENT,
	DEFAULT_AXNUI_THEME,
	DEFAULT_AXNUI_LAYOUT
} from "@iddataweb/idw-common-ui/constants";
import { AXNUIFormContentSlotContentTypes, InternationalAddressCountryTypes, LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { ApplicationState, VerificationConfigurationState } from "@idw-types/state";
import { config } from "node-config-ts";

export const getVerificationConfigurationState = (state: ApplicationState): VerificationConfigurationState => {
	return state.verificationConfiguration;
};

export const getVerificationConfiguration = createSelector(
	getVerificationConfigurationState,
	(state: VerificationConfigurationState): Partial<ConfigurationResponse> => {
		return state.config || {};
	});

export const getAsi = createSelector(getVerificationConfiguration, (configuration) => {
	return configuration.asi || "";
});

export const getAPIKey = createSelector(getVerificationConfiguration, (configuration) => {
	return configuration.apikey;
});

export const getConfigurationResponseProperties = createSelector(getVerificationConfiguration, (configuration): Partial<ConfigurationResponseProperties> => {
	return configuration.properties || {};
});

export const getConfigurationTheme = createSelector(getConfigurationResponseProperties, (properties): AXNUITheme => {
	const useThemedUI = properties.useThemedUI;
	const theme = properties.theme;
	if (useThemedUI && theme && Object.keys(theme).length > 0) {
		return merge({}, DEFAULT_AXNUI_THEME, theme);
	}
	return DEFAULT_AXNUI_THEME;
});

export const getConfigurationLayout = createSelector(getConfigurationResponseProperties, (properties): AXNUILayout => {
	const useThemedUI = properties.useThemedUI;
	const layout = properties.layout;
	if (useThemedUI && layout && Object.keys(layout).length > 0) {
		return merge({}, DEFAULT_AXNUI_LAYOUT, layout);
	}
	return DEFAULT_AXNUI_LAYOUT;
});

export const getConfigurationContent = createSelector(getConfigurationResponseProperties, (properties: Partial<ConfigurationResponseProperties>) => {
	const content = properties.content;
	if (content && Object.keys(content).length > 0) {
		return content;
	}

	return DEFAULT_AXNUI_CONTENT;
});

export const getConfirmButtonTheme = createSelector(getConfigurationTheme, (theme: AXNUITheme) => {
	return theme.confirmButton || null;
});

export const getInnerBackgroundColor = createSelector(getConfigurationTheme, (theme: AXNUITheme) => {
	return theme.general?.innerBackgroundColor || null;
});

export const getGeneralTheme = createSelector(getConfigurationTheme, (theme: AXNUITheme) => {
	return theme.general || null;
});

export const getFormPanelLayout = createSelector(getConfigurationLayout, (layout: AXNUILayout) => {
	return layout.formPanel;
});

export const getFormPanelTheme = createSelector(getConfigurationTheme, (theme: AXNUITheme) => {
	return theme.formPanel;
});

export const getUseExplicitConsentInput = createSelector(getConfigurationLayout, (layout: AXNUILayout) => {
	return (layout.helpText?.postFormHelpText?.order || []).includes(AXNUIFormContentSlotContentTypes.USE_EXPLICIT_CONSENT_INPUT) || false;
});

export const getLanguage = createSelector(getVerificationConfiguration, (configuration): string => {
	return configuration.language || null;
});

export const getLocalizeStrings = createSelector(getConfigurationResponseProperties, (properties): boolean => {
	return !!properties.localizeStrings;
});

export const getMergeThemingAndLocalizedFile = createSelector(getConfigurationResponseProperties, (properties): boolean => {
	return !!properties.mergeThemingAndLocalizedFile;
});

export const getUseThemingForAttributeLabels = createSelector(getConfigurationResponseProperties, (properties): boolean => {
	return !!properties.useThemingForAttributeLabels;
});

export const getLocalizeFileNamespace = createSelector(getConfigurationResponseProperties, (properties): string => {
	return properties.localizeFileNamespace || null;
});

export const getDisclaimer = createSelector(getConfigurationResponseProperties, (properties): string => {
	return properties.disclaimer || null;
});

export const getCancelButtonURL = createSelector(getConfigurationResponseProperties, (properties): string => {
	return properties.cancelButtonURL || null;
});

export const getUseThemedUI = createSelector(getConfigurationResponseProperties, (properties): boolean => {
	return !!properties.useThemedUI;
});

export const getThemeTextColor = createSelector(getConfigurationTheme, (theme: AXNUITheme): string => {
	return theme.general.mainTextColor;
});

export const getDCUIPreverify = createSelector(getConfigurationResponseProperties, (properties): boolean => {
	return !!properties.dcuiPreverify;
});

export const getSyncDCUIRetryURL = createSelector(getConfigurationResponseProperties, (properties): string => {
	return properties.syncDCUIRetryURL || null;
});

export const getDontRenderUI = createSelector(getConfigurationResponseProperties, (properties): boolean => {
	return !!properties.dontRenderUI;
});

export const getAutoSubmitPrefilledData = createSelector(getConfigurationResponseProperties, (properties): boolean => {
	return !!properties.autoSubmitPrefilledData;
});

export const getHeadErrorToFooter = createSelector(getConfigurationResponseProperties, (properties): boolean => {
	return !!properties.headErrorToFooter;
});

export const getHeadErrorMessage = createSelector(getConfigurationResponseProperties, (properties): string => {
	return properties.headerror || "";
});

export const getGooglePlacesAPIKey = createSelector(getConfigurationResponseProperties, (properties): string => {
	if (config.APPLICATION_ENVIRONMENT === "local") {
		return "AIzaSyBFoVLKk42IOfLUdHwWkw5Pr5Z6NsEFbD0";
	}
	return properties.googlePlacesAPIKey || "AIzaSyBFoVLKk42IOfLUdHwWkw5Pr5Z6NsEFbD0";
});

export const getUseSuffixInFullName = createSelector(getConfigurationResponseProperties, (properties): boolean => {
	return !!properties.useSuffixInFullName;
});

export const getCustomLanguages = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.customLanguages?.split(',') || [];
});

export const getAttributeInputList = createSelector(getVerificationConfiguration, (configuration) => {
	return configuration.AttributeInputList || [];
});

export const getAvailableLanguages = createSelector([getConfigurationResponseProperties, getCustomLanguages], (properties, customLanguages) => {
	const languageObj = properties.availableLanguages || {};
	const list = customLanguages.map((languageCode: LocaleTypes) => {
		return { code: languageCode, name: languageObj[languageCode] || languageCode };
	});
	return list;
});

const mapAvailableCountries = (availableCountries: any) => {
	if (!availableCountries) {
		return [];
	}
	return Object.keys(availableCountries).map((countryCode) => {
		return { value: countryCode, label: availableCountries[countryCode] };
	});
};


export const getAvailableCountries = createSelector(getConfigurationResponseProperties, (properties) => {
	const countryObj = properties.availableCountries || {};
	return mapAvailableCountries(countryObj);
});

export const getPageTitle = createSelector(getVerificationConfiguration, (configuration) => {
	const defaultTitle = configuration.serviceName || "New RP Service";
	let title = configuration.properties?.title;
	if (!title) {
		title = configuration.properties?.customTitle || defaultTitle;
	}
	return title;
});

export const getPageFavicon = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.favicon || null;
});

export const getTMXAutoSubmit = createSelector(getConfigurationResponseProperties, (properties): boolean => {
	return !!properties.tmxAutosubmit;
});

export const getTMXIDWJS = createSelector(getConfigurationResponseProperties, (properties): string => {
	return properties.tmxIDWJS || null;
});

export const getTMXSessionId = createSelector(getConfigurationResponseProperties, (properties): string => {
	return properties.tmx_sessionid || null;
});

export const getTMXOrgId = createSelector(getConfigurationResponseProperties, (properties): string => {
	return properties.tmxOrgID || null;
});

export const getTMXPageId = createSelector(getConfigurationResponseProperties, (properties): string => {
	return properties.tmxPageID || null;
});

export const getTMXProfilingDomain = createSelector(getConfigurationResponseProperties, (properties): string => {
	return properties.tmxProfilingDomain || null;
});

export const getAsyncInterval = createSelector(getConfigurationResponseProperties, (properties): number => {
	return properties.asyncinterval || 3000;
});

export const getDoDCUI = createSelector(getConfigurationResponseProperties, (properties): boolean => {
	return properties.doDCUI || false;
});

export const getTmxInDCUI = createSelector(getConfigurationResponseProperties, (properties): boolean => {
	return properties.tmxInDCUI || false;
});

export const getIsSuccessPageEnabled = createSelector(getConfigurationLayout, (layout: AXNUILayout) => {
	return layout.isSuccessPageEnabled || false;
});

export const getRPName = createSelector(getVerificationConfiguration, (configuration) => {
	return configuration.rpName || "";
});

export const getIDPName = createSelector(getVerificationConfiguration, (configuration) => {
	return configuration.idpName || "";
});

export const getThemeGeneralInnerBackgroundColor = createSelector(getConfigurationTheme, (theme: AXNUITheme) => {
	return theme.general?.innerBackgroundColor || null;
});

export const getMaxPinSendCount = createSelector(getConfigurationResponseProperties, (properties): number => {
	return properties.MaxPinSendCount || null;
});

export const getOOBPinUrl = createSelector(getVerificationConfiguration, (configuration) => {
	return configuration.oobPINURL || null;
});

export const getVerifyToken = createSelector(getVerificationConfiguration, (configuration) => {
	return configuration.verifyToken;
});

export const getVerifyURL = createSelector(getVerificationConfiguration, (configuration) => {
	return configuration.verifyURL;
});

export const getIsPolicyDecisionRetry = createSelector(getConfigurationResponseProperties, (properties): boolean => {
	return !!properties.isPolicyDecisionRetry;
});

export const getLogoUrl = createSelector(getConfigurationResponseProperties, (properties) => {
	if (properties.useThemedUI) {
		return null;
	} else {
		return properties.logo || properties.customLogo || null;
	}
});

export const getLogoPosition = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.logoPosition || "center";
});

export const getFormHelpTextType = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.formHelpTextType || null;
});

export const getCustomFormHelpText = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.customFormHelpText || null;
});

export const getShowBokuConsentFooter = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.showBokuConsentFooter || null;
});

export const getCountryCode = createSelector(getVerificationConfiguration, (configuration) => {
	return configuration.countryCode || InternationalAddressCountryTypes.US;
});

export const getRedirectToCallbackUrlOnError = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.redirectToCallbackUrlOnError || false;
});

export const getOidcCallbackURL = createSelector(getVerificationConfiguration, (configuration) => {
	return configuration.oidcCallbackURL || null;
});

export const getIsLanguageSelectorEnabled = createSelector(getConfigurationLayout, (layout) => {
	return layout.isLanguageSelectorEnabled;
});
